/**
 * Vendored copy of packages/panel-data-kit's createResourceQueryConfig.
 *
 * Not consumed as a package dependency: this app's Railway build context is
 * scoped to apps/opticomm-admin-panel, so a portal-linked sibling package
 * under packages/ is never copied into the Docker build and fails to resolve.
 * Keep this file in sync with
 * packages/panel-data-kit/src/create-resource-query-config.ts if either changes.
 */

/**
 * A bare react-query key: an array of serializable segments. Declared locally
 * (not imported from `@tanstack/react-query`) so this file never depends on
 * react-query at all — see the note on `createResourceQueryConfig` below for why.
 */
export type QueryKey = readonly unknown[]

export type ResourceQueryKeys<TId, TDetailQuery, TListQuery> = {
  detail: (id: TId, query?: TDetailQuery) => QueryKey
  list: (query?: TListQuery) => QueryKey
}

export type ResourceTransport<TDetailResponse, TListResponse, TId, TDetailQuery, TListQuery> = {
  retrieve: (id: TId, query?: TDetailQuery) => Promise<TDetailResponse>
  list: (query?: TListQuery) => Promise<TListResponse>
}

export type ResourceQueryConfig<TDetailResponse, TListResponse, TId, TDetailQuery, TListQuery> = {
  queryKeys: ResourceQueryKeys<TId, TDetailQuery, TListQuery>
  transport: ResourceTransport<TDetailResponse, TListResponse, TId, TDetailQuery, TListQuery>
}

/**
 * Builds the `{ queryKey, queryFn }` pair for a resource's detail/list queries —
 * the piece that previously got copy-pasted per resource per panel, differing
 * only in the transport (`sdk.admin.<resource>.retrieve` vs.
 * `fetchQuery('/vendor/<resource>/...')`).
 *
 * Deliberately does NOT call `useQuery` itself or import anything from
 * `@tanstack/react-query`: the admin and vendor panels are separate projects
 * pinning different react-query versions, and a shared implementation whose
 * compiled output references one specific version's types causes real
 * cross-version type conflicts when consumed by an app pinning a different
 * one. Each panel calls its own locally-imported `useQuery` with the config
 * this returns.
 */
export function createResourceQueryConfig<
  TDetailResponse,
  TListResponse,
  TId = string,
  TDetailQuery = unknown,
  TListQuery = unknown,
>(config: ResourceQueryConfig<TDetailResponse, TListResponse, TId, TDetailQuery, TListQuery>) {
  return {
    detailQuery: (id: TId, query?: TDetailQuery) => ({
      queryKey: config.queryKeys.detail(id, query),
      queryFn: () => config.transport.retrieve(id, query),
    }),
    listQuery: (query?: TListQuery) => ({
      queryKey: config.queryKeys.list(query),
      queryFn: () => config.transport.list(query),
    }),
  }
}
